export interface BearingParams { c: number; phi: number; gammaBelow: number; gammaAbove: number; Df: number; Beff: number; N: number; H: number; /** taban eğimi α (rad, topuk alçak); yoksa 0 */ alpha?: number }
export interface BearingCapacityResult {
  qk: number; Nc: number; Nq: number; Ngamma: number;
  dc: number; dq: number; dgamma: number; ic: number; iq: number; igamma: number;
  bc: number; bq: number; bgamma: number;
  qbar: number; termC: number; termQ: number; termGamma: number;
}

/**
 * TBDY 2018 Denk. (16.8a)-(16.8b): qk = c·Nc·dc·ic·bc + q̄·Nq·dq·iq·bq + ½·γ·B'·Nγ·dγ·iγ·bγ  (şerit temel: s = 1, g = 1).
 * Derinlik katsayıları Brinch Hansen, eğiklik katsayıları EC7 Ek D (Vesic, m = 2). Taban eğimi katsayıları b — TBDY 16.8.3.2
 * "literatüre dayanan ve genel kabul görmüş bağıntılar": EN 1997-1 Ek D (D.3, D.4): b_q = b_γ = (1 − α·tanφ)²,
 * b_c = b_q − (1 − b_q)/(N_c·tanφ); drenajsız b_c = 1 − 2α/(π + 2). α = 0 → b = 1 (yatay taban, sonuçlar değişmez).
 */
export function bearingCapacity16_8a(p: BearingParams): BearingCapacityResult {
  const { c, phi, Df, Beff, N, H } = p;
  const tanPhi = Math.tan(phi);
  const phiZero = phi < 1e-9;
  const Nq = phiZero ? 1 : Math.exp(Math.PI * tanPhi) * Math.tan(Math.PI / 4 + phi / 2) ** 2;
  const Nc = phiZero ? Math.PI + 2 : (Nq - 1) / tanPhi;
  const Ngamma = phiZero ? 0 : 2 * (Nq - 1) * tanPhi;

  const k = Beff > 0 ? (Df / Beff <= 1 ? Df / Beff : Math.atan(Df / Beff)) : 0;
  const dq = 1 + 2 * tanPhi * (1 - Math.sin(phi)) ** 2 * k;
  const dc = phiZero ? 1 + 0.4 * k : dq - (1 - dq) / (Nc * tanPhi);
  const dgamma = 1;

  let iq = 1, igamma = 1, ic = 1;
  if (H > 1e-12) {
    if (phiZero) {
      ic = c > 0 && Beff > 0 ? 0.5 * (1 + Math.sqrt(Math.max(0, 1 - H / (Beff * c)))) : 1;
    } else {
      const denom = N + Beff * c / tanPhi;
      const base = Math.max(0, 1 - H / denom);
      iq = base ** 2;
      igamma = base ** 3;
      ic = iq - (1 - iq) / (Nc * tanPhi);
    }
  }

  const alpha = p.alpha ?? 0;
  let bq = 1, bgamma = 1, bc = 1;
  if (alpha > 1e-12) {
    if (phiZero) bc = 1 - (2 * alpha) / (Math.PI + 2);
    else { bq = Math.max(0, 1 - alpha * tanPhi) ** 2; bgamma = bq; bc = bq - (1 - bq) / (Nc * tanPhi); }
  }

  const qbar = p.gammaAbove * Df;
  const termC = c * Nc * dc * ic * bc;
  const termQ = qbar * Nq * dq * iq * bq;
  const termGamma = 0.5 * p.gammaBelow * Beff * Ngamma * dgamma * igamma * bgamma;
  return { qk: termC + termQ + termGamma, Nc, Nq, Ngamma, dc, dq, dgamma, ic, iq, igamma, bc, bq, bgamma, qbar, termC, termQ, termGamma };
}
